import {
  type CreateQuizSetReq,
  type MaterialDto,
  type QuizData,
  type UpdateQuizSetReq,
} from '@/api';
import { SchoolMapper } from '@/shared/category/util';
import { toLegacyShowIncorrectAnswer } from '@/shared/utils/answerRevealMode';

import { type BuildSchemaInputType } from '../form/schema';

import { getEffectiveCurriculumList } from './curriculumFingerprint';

export interface TransformOptions {
  action: 'CREATE' | 'UPDATE' | 'DUPLICATE';
  quizSetId?: string;
  originQuizSetId?: string;
}

/**
 * Transforms form data from the build form into API request format
 * @param formData The form data from React Hook Form
 * @param options Transform options including action type and IDs
 * @returns API request object for creating or updating a quiz set
 */
export function transformBuildFormToApiRequest(
  formData: BuildSchemaInputType,
  options: TransformOptions,
): CreateQuizSetReq | UpdateQuizSetReq {
  const {
    title,
    templateId,
    subject,
    subSubject,
    semesterId,
    publisher,
    customPublisher,
    quizList,
    isLearningMaterialEnabled,
    materials,
    isPublic,
    isAllowPass,
    answerRevealMode,
    isLastSchoolInfoSaved,
    isNextQuizsetAvailable,
    nextQuizsetId,
    aiTranslationLanguages,
    isAiTranslationEnabled,
    aiTutor,
    aiTutorEnabled,
  } = formData;

  const baseRequest = {
    title: title,
    templateId,
    categoryTreeData: transformCategoryTreeData({
      subject,
      subSubject,
      semesterId,
      publisher,
      customPublisher,
    }),
    quizList: transformQuizList(quizList),
    quizsetOptions: transformQuizsetOptions({
      isPublic,
      isAllowPass,
      answerRevealMode,
      isLastSchoolInfoSaved,
      isAiTranslationEnabled,
      aiTranslationLanguages,
    }),
    materials: isLearningMaterialEnabled ? transformMaterials(materials) : [],
    isNextQuizsetAvailable: !nextQuizsetId ? false : isNextQuizsetAvailable,
    nextQuizsetId,
    aiTranslationLanguages: isAiTranslationEnabled
      ? aiTranslationLanguages
      : [],
    aiTutorId: aiTutorEnabled ? aiTutor?.id : undefined,
  };

  // Add action-specific fields
  if (options.action === 'UPDATE' && options.quizSetId) {
    return {
      ...baseRequest,
      quizSetId: options.quizSetId,
    } as UpdateQuizSetReq;
  }

  if (options.action === 'DUPLICATE' && options.originQuizSetId) {
    return {
      ...baseRequest,
      originQuizSetId: options.originQuizSetId,
    } as CreateQuizSetReq;
  }

  return baseRequest as CreateQuizSetReq;
}

/**
 * Transforms category tree data for the API
 */
function transformCategoryTreeData(data: {
  subject: string;
  subSubject?: string;
  semesterId?: string;
  publisher?: string;
  customPublisher?: string;
}) {
  const { subject, subSubject, semesterId, publisher, customPublisher } = data;
  const categoryTreeId = subSubject || subject;
  const publisherValue = getPublisherValue(publisher, customPublisher);

  return {
    categoryTreeId,
    ...(semesterId && { schoolSemester: semesterId.toString() }),
    ...(publisherValue && { publisher: publisherValue }),
  };
}

/**
 * Gets the final publisher value based on selection
 */
function getPublisherValue(
  publisher?: string,
  customPublisher?: string,
): string {
  if (!publisher) return '';
  // Check if publisher is "기타" (etc/other in Korean)
  return publisher === '기타' || SchoolMapper.isEtc(publisher)
    ? customPublisher || ''
    : publisher;
}

/**
 * Transforms the quiz list for the API
 */
function transformQuizList(
  quizList: BuildSchemaInputType['quizList'],
): QuizData[] {
  return quizList.map((quiz, index) => transformQuizItem(quiz, index));
}

/**
 * Transforms a single quiz item based on its type
 */
function transformQuizItem(
  quiz: BuildSchemaInputType['quizList'][0],
  index: number,
): QuizData {
  const baseQuiz = {
    id: String(quiz.id),
    order: index,
    imageUrl: quiz.questionImageUrl ?? '',
    hint: quiz.hint,
    // 성취기준 부여 이후 편집된 문항은 매핑 유효성이 깨졌으므로 빈 리스트로 제출한다.
    curriculumList: getEffectiveCurriculumList(quiz) ?? [],
  };

  switch (quiz.type) {
    case 'subjective':
      return {
        ...baseQuiz,
        type: 'subjective',
        subjectiveData: {
          question: quiz.question || '',
          questionRichText: quiz.questionRichText,
          answerList: (quiz.subjectiveData.answerList ?? []).map(
            answer => answer.text,
          ),
        },
      } as QuizData;

    case 'multiple':
      return {
        ...baseQuiz,
        type: 'multiple',
        multipleData: {
          question: quiz.question || '',
          questionRichText: quiz.questionRichText,
          choiceListV2: quiz.multipleData.choiceList ?? [],
          answerList: quiz.multipleData.choiceList
            .filter(choice => choice.isAnswer)
            .map(answerChoice =>
              String(quiz.multipleData.choiceList.indexOf(answerChoice)),
            ),
        },
      } as QuizData;

    case 'ox':
      return {
        ...baseQuiz,
        type: 'ox',
        oxData: {
          question: quiz.question || '',
          questionRichText: quiz.questionRichText,
          answer: quiz.oxData.answer ?? '',
        },
      } as QuizData;

    case 'essay':
      return {
        ...baseQuiz,
        type: 'essay',
        essayData: {
          question: quiz.question || '',
          questionRichText: quiz.questionRichText,
        },
      } as QuizData;

    default:
      // This should never happen due to TypeScript's exhaustive checking
      throw new Error(`Unknown quiz type: ${(quiz as any).type}`);
  }
}

/**
 * Transforms quiz set options for the API
 */
function transformQuizsetOptions(options: {
  isPublic: boolean;
  isAllowPass: boolean;
  answerRevealMode: BuildSchemaInputType['answerRevealMode'];
  isLastSchoolInfoSaved: boolean;
  isAiTranslationEnabled: boolean;
  aiTranslationLanguages: string[];
}) {
  const {
    isPublic,
    isAllowPass,
    answerRevealMode,
    isLastSchoolInfoSaved,
    isAiTranslationEnabled,
    aiTranslationLanguages,
  } = options;
  const showIncorrectAnswer = toLegacyShowIncorrectAnswer(answerRevealMode);

  return {
    isPublic,
    isAllowPass,
    answerRevealMode,
    showIncorrectAnswer,
    isLastSchoolInfoSaved,
    isAiTranslationEnabled:
      isAiTranslationEnabled && aiTranslationLanguages.length > 0,
  };
}

/**
 * Transforms materials array for the API
 */
function transformMaterials(
  materials: BuildSchemaInputType['materials'],
): MaterialDto[] {
  return materials.map(material => ({
    type: material.type,
    url: material.url,
    originalFileName: material.originalFileName,
    allowReopenDuringPlay: material.allowReopenDuringPlay,
    fileSize: material.fileSize,
  }));
}
